// Pure helpers for the playlist delivery state machine (spec §3.2).

export const DELIVERY_ACTIVE = ['sending', 'building'];

// The failures after which every item is already on the station.
const BUILD_FAILURE_KINDS = ['station', 'timeout', 'not_available', 'auth', 'invalid', 'not_found'];

const BUILD_KIND_BY_CODE = {
  station_timeout: 'timeout',
  not_available: 'not_available',
  unauthorized: 'auth',
  invalid_playlist: 'invalid',
  agent_not_found: 'not_found',
};

const GONE_RE = /not[ _-]?found|404|no longer|deleted/i;
const OFFLINE_RE = /offline|not connected|disconnected/i;

// The station's name, or copy S-8 (as AgentPickerPopover does).
export const stationLabel = (agents, agentId) => {
  const agent = agents?.find((a) => a.agent_id === agentId);
  return agent?.description || agent?.name || `Media Station ${String(agentId ?? '').slice(0, 8)}`;
};

export const isStationOnline = (agents, agentId) =>
  agents?.some((a) => a.agent_id === agentId && a.status === 'online') ?? false;

/** Classify a failed `send-designs-to-agent` call: `offline`, `gone` or `item`. */
export const classifySendError = (error, stationOnline) => {
  const message = error?.message ?? '';
  if (!stationOnline || OFFLINE_RE.test(message)) return 'offline';
  if (error?.status === 404 || GONE_RE.test(message)) return 'gone';
  return 'item';
};

/**
 * Classify a failed `deliver-playlist` call. A station failure that names
 * missing items becomes `missing` with the matching item keys.
 */
export const classifyBuildError = (error, items) => {
  if (error?.code === 'station_failure') {
    const missingItemKeys = matchMissingItemKeys(items, error.stationDetails?.missingItems);
    return missingItemKeys.length > 0
      ? { kind: 'missing', missingItemKeys }
      : { kind: 'station', missingItemKeys: [] };
  }
  return { kind: BUILD_KIND_BY_CODE[error?.code] ?? 'station', missingItemKeys: [] };
};

/**
 * Map the station's `missingItems` ([{designId}]) to item keys: by design id,
 * and for duplicates by order of appearance (spec §8).
 */
export const matchMissingItemKeys = (items, missingItems) => {
  const used = new Set();
  return (missingItems ?? []).flatMap((missing) => {
    const designId = missing?.designId ?? missing?.design_id;
    const match = items.find((item) => item.designId === designId && !used.has(item.itemKey));
    if (!match) return [];
    used.add(match.itemKey);
    return [match.itemKey];
  });
};

/**
 * One tile's delivery state: `idle`, `queued`, `sending`, `sent`, `failed`,
 * `gone` or `missing` (the tile's `data-status`).
 */
export const tileStatusFor = (delivery, itemKey) => {
  const { status, currentKey, pendingKeys = [], failedKind, failedKey, missingItemKeys = [] } = delivery ?? {};
  switch (status) {
    case 'sending':
      if (itemKey === currentKey) return 'sending';
      return pendingKeys.includes(itemKey) ? 'queued' : 'sent';
    case 'building':
    case 'done':
      return 'sent';
    case 'failed':
      if (failedKind === 'missing') return missingItemKeys.includes(itemKey) ? 'missing' : 'sent';
      if (BUILD_FAILURE_KINDS.includes(failedKind)) return 'sent';
      if (itemKey === failedKey) return failedKind === 'gone' ? 'gone' : 'failed';
      return pendingKeys.includes(itemKey) ? 'queued' : 'sent';
    default:
      return 'idle';
  }
};

/**
 * What Retry does for a failure (spec S7): `resume` re-sends the pending
 * items, `missing` re-sends the missing ones, `rebuild` only rebuilds,
 * `choose` reopens the confirm sheet, `none` means no Retry.
 */
export const retryModeFor = (failedKind) => {
  if (['item', 'gone', 'offline'].includes(failedKind)) return 'resume';
  if (failedKind === 'missing') return 'missing';
  if (['station', 'timeout', 'not_available'].includes(failedKind)) return 'rebuild';
  if (failedKind === 'not_found') return 'choose';
  return 'none';
};

export const fileBaseName = (path) => (path ? String(path).split(/[\\/]/).pop() : '');
